import {Component, OnInit, signal} from '@angular/core';
import {EquipeDto} from './model/equipe.dto';
import {UtilisateurSigalett} from '../../../shared/models/utilisateur-sigalett.model';
import {UtilisateurDto} from '../../../shared/dto/utilisateur.dto';
import {AlertService, ConfirmationAction, ConfirmationData, ConfirmationModalService, isNotNullOrUndefined, isNullOrUndefined} from 'sigale-angular';
import {EquipeService} from './service/equipe.service';
import {AuthService} from '../../../shared/services/auth-service.service';
import {finalize} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {OngletGestionEquipeComponent} from '../onglet-gestion-equipe.component';
import {Mode} from '../../../shared/components/selection-zone/model/mode.enum';
import {ZoneDeTravailAvecSiteDto} from '../../../shared/dto/zone-de-travail-avec-site.dto';
import {NouveauMembreDto} from './model/nouveau-membre.dto';

@Component({
	selector: 'app-equipe',
	templateUrl: './equipe.component.html',
	styleUrl: './equipe.component.scss'
})
export class EquipeComponent extends OngletGestionEquipeComponent implements OnInit {
	readonly NOM_ALERTE_ERREUR_GENERALE = 'tabEquipeGestionEquipe';

	saisieNomEquipe = signal(false);
	loadingModifierEquipe = signal(false);

	equipe: EquipeDto;
	nomEquipe: string;
	utilisateursSuggestions: UtilisateurSigalett[];
	utilisateurAAjouter: UtilisateurDto;
	utilisateursSelectionnes: UtilisateurDto[] = [];
	protected readonly isNotNullOrUndefined = isNotNullOrUndefined;

	constructor(
		private equipeService: EquipeService,
		private alertService: AlertService,
		private translateService: TranslateService,
		authService: AuthService,
		private confirmationModalService: ConfirmationModalService
	) {
		super(authService);
	}

	ngOnInit(): void {
		this.chargerDonnees();
	}

	protected chargerDonneesImpl() {
		this.equipeService.recupererEquipeParResponsable(this.loginUtilisateurConnecte)
			.pipe(finalize(() => this.loading = false))
			.subscribe({
				next: equipeDto => {
					this.equipe = equipeDto;
					if (isNotNullOrUndefined(this.equipe.id)) {
						this.nomEquipe = this.equipe.nom;
					}
					else {
						this.saisieNomEquipe.set(true);

					}
				},
				error: error => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	/**
	 * Créer une équipe avec le nom renseigné si l'équipe n'a pas d'id. Sinon modifie le nom d'équipe.
	 */
	public creerModifierEquipe(): void {
		if (isNullOrUndefined(this.nomEquipe) || this.nomEquipe === '') {
			this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
			this.alertService.errorSansCode(this.translateService.instant('EQUIPE.ERREUR.NOM_EQUIPE_VIDE'), this.NOM_ALERTE_ERREUR_GENERALE);
			this.nomEquipe = this.equipe.nom;
			return;
		}

		this.loadingModifierEquipe.set(true);
		if (isNullOrUndefined(this.equipe.id)) {
			this.equipeService.creerEquipe({
				nom: this.nomEquipe,
				loginResponsable: this.loginUtilisateurConnecte,
				membres: [],
				zonesDeTravailAutorisees: []
			})
				.pipe(finalize(() => this.loadingModifierEquipe.set(false)))
				.subscribe({
					next: equipeDto => {
						this.equipe = equipeDto;
						this.saisieNomEquipe.set(false);
					},
					error: error => {
						this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
						this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
					}
				});
		}
		else {
			this.equipeService.mettreAJourNomEquipe(this.equipe.id, this.nomEquipe)
				.pipe(finalize(() => this.loadingModifierEquipe.set(false)))
				.subscribe({
					next: equipeDto => {
						this.equipe = equipeDto;
						this.saisieNomEquipe.set(false);
					},
					error: error => {
						this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
						this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
					}
				});
		}
	}

	/**
	 * Ajoute un utilisateur à l'équipe.
	 */
	public ajouterUtilisateur(forcerChangementEquipe: boolean): void {
		if (isNullOrUndefined(this.utilisateurAAjouter)) {
			this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
			this.alertService.errorSansCode(this.translateService.instant('EQUIPE.ERREUR.UTILISATEUR_NON_SAISI'), this.NOM_ALERTE_ERREUR_GENERALE);
			return;
		}

		const nouveauMembre: NouveauMembreDto = new NouveauMembreDto(this.utilisateurAAjouter.login, forcerChangementEquipe);

		this.equipeService.ajouterUtilisateur(this.equipe.id, nouveauMembre)
			.subscribe({
				next: equipeDto => {
					this.equipe = equipeDto;
					this.utilisateurAAjouter = null;
				},
				error: error => {
					// Si on a une erreur avec le code 1000 (Code EquipeException) alors l'utilisateur possède déjà uen équipe, il faut la confirmation de l'utilisateur.
					if (error.error.code == 1000) {
						const confirmationActions = [];
						const boutonOui = new ConfirmationAction('GLOBAL.OUI');
						const boutonNon = new ConfirmationAction('GLOBAL.NON');
						boutonOui.onClick.subscribe(() => {
							this.alertService.clear('confirmationModalAlert');
							this.ajouterUtilisateur(true);
							this.confirmationModalService.fermer();
						});
						boutonNon.onClick.subscribe(() => {
							this.alertService.clear('confirmationModalAlert');
							this.utilisateurAAjouter = null;
							this.confirmationModalService.fermer();
						});
						boutonNon.type = 'link';
						confirmationActions.push(boutonOui);
						confirmationActions.push(boutonNon);
						const confirmationData = new ConfirmationData(
							'EQUIPE.CONFIRMATION_AJOUT_UTILISATEUR',
							error.error.message,
							confirmationActions
						);
						this.confirmationModalService.ouvrir(confirmationData);
					}
					else {
						this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
						this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
					}
				}
			});
	}

	/**
	 * Retire les utilisateurs sélectionnés de l'équipe.
	 */
	public retirerUtilisateurs(): void {
		if (this.utilisateursSelectionnes.length <= 0) {
			this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
			this.alertService.errorSansCode(this.translateService.instant('EQUIPE.ERREUR.AUCUN_UTILISATEUR_SELECTIONNE'), this.NOM_ALERTE_ERREUR_GENERALE);
			return;
		}

		const logins: string[] = this.utilisateursSelectionnes.map(utilisateur => utilisateur.login);

		this.equipeService.retirerUtilisateur(this.equipe.id, logins)
			.subscribe({
				next: equipeDto => {
					this.equipe = equipeDto;
				},
				error: error => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	public activerDesactiverSaisiNomEquipe(): void {
		if (this.saisieNomEquipe() && isNotNullOrUndefined(this.equipe)) {
			this.nomEquipe = this.equipe.nom;
		}
		this.saisieNomEquipe.set(!this.saisieNomEquipe());
	}

	public isButtonModifierNomEquipeDisabled(): boolean {
		return this.nomEquipe === '' ||
			(isNotNullOrUndefined(this.equipe) && this.nomEquipe === this.equipe.nom);
	}

	onAjoutRetraitZones(zonesSelectionnees: ZoneDeTravailAvecSiteDto[]): void {
		const zonesDeTravailIds = zonesSelectionnees.map(zone => zone.id);
		this.equipeService.modifierZoneDeTravailAutorisees(this.equipe.id, zonesDeTravailIds)
			.subscribe({
				next: () => {
					this.equipe.zonesDeTravailAutorisees = zonesSelectionnees;
				},
				error: error => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	protected readonly Mode = Mode;

	protected readonly isNullOrUndefined = isNullOrUndefined;
}
